// Tela de cobranças recorrentes (M12).
// Lista, cria, edita, pausa/ativa, arquiva. Modo teste: prévia + envio pro dono.
//
// Copy educativa importante: recorrência só faz sentido pra MENSALIDADES.
// Cobrança única/eventual é feita direto pelo wizard "Nova cobrança".
// WhatsApp precisa crédito no mês da cobrança — sem crédito manda só e-mail.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Typography, Space, Button, Table, Tag, Modal, Form, Input, InputNumber,
  Select, Switch, message, Popconfirm, Alert, Row, Col, Divider, Empty, Tooltip,
} from 'antd';
import {
  PlusOutlined, PauseCircleOutlined, PlayCircleOutlined, EditOutlined,
  DeleteOutlined, ExperimentOutlined, InfoCircleOutlined, CalendarOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import api from '../../../../services/api';
import { getActiveTeamId } from '../../../../utils/activeTeam';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const PRIMARY = '#128C7E';
const BRAND = '#25D366';
const fmtBRL = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;

const STATUS_META = {
  active: { color: 'green', label: 'Ativa' },
  paused: { color: 'orange', label: 'Pausada' },
  archived: { color: 'default', label: 'Arquivada' },
};

// dia efetivo do mês (frontend só pra exibir "dia N ou último")
function dayLabel(d) {
  if (!Number.isFinite(d)) return '—';
  if (d === 31) return 'Dia 31 (ou último dia do mês)';
  return `Dia ${d}`;
}

// Helper de gross-up (espelha lógica do backend chargeRecurrenceRunner).
// Prioriza PIX (mais comum) — se pagador escolher cartão, time recebe menos.
const MP_FEE = { pix: { pct: 0.99 }, credit_card: { pct: 4.99 } };
function grossUp(netAmount, methods) {
  const f = methods?.pix ? MP_FEE.pix : MP_FEE.credit_card;
  return Number(netAmount) / (1 - f.pct / 100);
}

export default function RecurrencesPage() {
  const teamId = getActiveTeamId();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [testOpen, setTestOpen] = useState(false);
  const [testItem, setTestItem] = useState(null);
  const [testPreview, setTestPreview] = useState(null);
  const [testLoading, setTestLoading] = useState(false);

  const load = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);
    try {
      const [r, g] = await Promise.all([
        api.get('/community/finance/charges/recurrences', { params: { team_id: teamId } }),
        api.get('/community/finance/contact-groups', { params: { team_id: teamId } }),
      ]);
      setItems(r.data.items || []);
      setGroups(g.data.items || g.data || []);
    } catch (e) {
      message.error('Não rolou carregar recorrências');
    } finally { setLoading(false); }
  }, [teamId]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (row) => { setEditing(row); setFormOpen(true); };

  const togglePause = async (row) => {
    try {
      const nextStatus = row.status === 'active' ? 'paused' : 'active';
      await api.put(`/community/finance/charges/recurrences/${row.id}`, {
        team_id: teamId, status: nextStatus,
      });
      message.success(nextStatus === 'active' ? 'Recorrência ativa de novo' : 'Recorrência pausada');
      load();
    } catch (e) { message.error(e.response?.data?.message || 'Falhou'); }
  };

  const archive = async (row) => {
    try {
      await api.delete(`/community/finance/charges/recurrences/${row.id}`, { params: { team_id: teamId } });
      message.success('Arquivada');
      load();
    } catch (e) { message.error(e.response?.data?.message || 'Falhou'); }
  };

  const openTest = async (row) => {
    setTestItem(row);
    setTestPreview(null);
    setTestOpen(true);
    setTestLoading(true);
    try {
      const r = await api.post(`/community/finance/charges/recurrences/${row.id}/test-preview`,
        { team_id: teamId });
      setTestPreview(r.data);
    } catch (e) {
      message.error(e.response?.data?.message || 'Prévia falhou');
    } finally { setTestLoading(false); }
  };

  const sendTest = async () => {
    if (!testItem) return;
    try {
      const r = await api.post(`/community/finance/charges/recurrences/${testItem.id}/test-send`,
        { team_id: teamId });
      if (r.data?.error) {
        message.warning(`Batch criado com aviso: ${r.data.error}`);
      } else {
        message.success('Teste enviado! Só pro seu contato de dono, marcado como is_test.');
      }
      setTestOpen(false);
    } catch (e) { message.error(e.response?.data?.message || 'Envio de teste falhou'); }
  };

  const columns = [
    {
      title: 'Nome do template',
      dataIndex: 'template_name',
      render: (v, row) => (
        <div>
          <Text strong>{v}</Text>
          <div><Text type="secondary" style={{ fontSize: 12 }}>{row.template_description || ''}</Text></div>
        </div>
      ),
    },
    {
      title: 'Dia',
      dataIndex: 'day_of_month',
      width: 180,
      render: (d) => dayLabel(d),
    },
    {
      title: 'Valor',
      dataIndex: 'amount_brl',
      width: 160,
      render: (v, row) => {
        if (!row.absorb_fee) return <Text strong>{fmtBRL(v)}</Text>;
        const cobrado = Math.round(grossUp(v, row.methods) * 100) / 100;
        return (
          <Tooltip title="Valor que você recebe (líquido) → valor cobrado do jogador (bruto), com taxa MP embutida.">
            <div>
              <Text strong>{fmtBRL(v)}</Text>
              <div style={{ fontSize: 11, color: '#faad14' }}>
                Cobra {fmtBRL(cobrado)}
              </div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Fonte',
      width: 200,
      render: (_, row) => (
        row.source_type === 'group'
          ? <span><Tag color="blue">Grupo</Tag>{row.group_name || '?'} <Text type="secondary" style={{ fontSize: 11 }}>({row.group_size || 0})</Text></span>
          : <span><Tag>Lista</Tag>{Array.isArray(row.snapshot_recipients) ? `${row.snapshot_recipients.length} contatos` : ''}</span>
      ),
    },
    {
      title: 'Próxima',
      dataIndex: 'next_run_at',
      width: 130,
      render: (v) => v ? dayjs(v).format('DD/MM/YYYY') : '—',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 100,
      render: (s) => {
        const m = STATUS_META[s] || { color: 'default', label: s };
        return <Tag color={m.color}>{m.label}</Tag>;
      },
    },
    {
      title: 'Ações',
      width: 200,
      render: (_, row) => (
        <Space size={4} wrap>
          <Tooltip title="Modo teste (envia só pro dono)">
            <Button size="small" icon={<ExperimentOutlined />} onClick={() => openTest(row)} />
          </Tooltip>
          <Tooltip title="Editar">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          </Tooltip>
          {row.status !== 'archived' && (
            <Tooltip title={row.status === 'active' ? 'Pausar' : 'Ativar'}>
              <Button size="small"
                icon={row.status === 'active' ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                onClick={() => togglePause(row)} />
            </Tooltip>
          )}
          {row.status !== 'archived' && (
            <Popconfirm title="Arquivar recorrência?" description="Ela para de gerar novos batches. Batches anteriores continuam."
              onConfirm={() => archive(row)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '16px 12px', maxWidth: 1200, margin: '0 auto' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}><CalendarOutlined /> Cobranças recorrentes</Title>
          <Text type="secondary">Mensalidades que caem no dia certo, todo mês, sem você precisar lembrar.</Text>
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={openNew}
            style={{ background: PRIMARY, borderColor: PRIMARY }}>
            Nova recorrência
          </Button>
        </Col>
      </Row>

      <Alert
        type="info" showIcon style={{ marginBottom: 12 }}
        message="Quando usar recorrência?"
        description={
          <div style={{ fontSize: 13 }}>
            Recorrência é só pra <b>mensalidades</b> (mensal, sempre no mesmo dia).
            Cobrança única ou eventual, faz pelo botão <b>Nova cobrança</b> normal.
            <br />
            <b>WhatsApp precisa de crédito no mês da cobrança.</b> Sem crédito, a cobrança sai só por e-mail.
          </div>
        }
      />

      <Card styles={{ body: { padding: 0 } }}>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={items}
          loading={loading}
          pagination={false}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <div>
                    <Text strong>Nenhuma recorrência configurada</Text>
                    <br /><Text type="secondary">Cria a primeira mensalidade e não precisa mais lembrar de cobrar todo mês.</Text>
                  </div>
                }
              >
                <Button type="primary" icon={<PlusOutlined />} onClick={openNew}
                  style={{ background: PRIMARY, borderColor: PRIMARY }}>
                  Nova recorrência
                </Button>
              </Empty>
            ),
          }}
        />
      </Card>

      <RecurrenceFormModal
        open={formOpen}
        editing={editing}
        groups={groups}
        teamId={teamId}
        onClose={() => setFormOpen(false)}
        onSaved={() => { setFormOpen(false); load(); }}
      />

      <Modal
        open={testOpen}
        title={<Space><ExperimentOutlined /> Modo teste</Space>}
        onCancel={() => setTestOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setTestOpen(false)}>Fechar</Button>,
          <Button key="send" type="primary" icon={<ThunderboltOutlined />}
            loading={testLoading} disabled={!testPreview}
            style={{ background: PRIMARY, borderColor: PRIMARY }}
            onClick={sendTest}>
            Enviar teste (só pra mim)
          </Button>,
        ]}
        width={640}
      >
        {testLoading && !testPreview ? (
          <Text type="secondary">Carregando prévia…</Text>
        ) : testPreview ? (
          <div>
            <Alert type="warning" showIcon style={{ marginBottom: 12 }}
              message="Como funciona o teste"
              description="Envia UMA cobrança pra você mesmo (contato do dono). Marcada como is_test — não entra na consolidação de valores em aberto e não conta como cobrança real do time." />
            <Card size="small">
              <Space direction="vertical" style={{ width: '100%' }} size={4}>
                <Text><b>Nome do batch:</b> {testPreview.batch_name}</Text>
                <Text><b>Descrição:</b> {testPreview.description}</Text>
                <Text><b>Destinatários reais (não são enviados no teste):</b> {testPreview.recipients_count}</Text>
                <Text><b>Total real:</b> {fmtBRL(testPreview.total_amount_brl)}</Text>
                {testItem?.absorb_fee && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Valor cobrado (com taxa MP embutida). Você recebe R$ {Number(testItem.amount_brl).toFixed(2).replace('.', ',')} por pessoa, cobra {fmtBRL(Math.round(grossUp(testItem.amount_brl, testItem.methods) * 100) / 100)}.
                  </Text>
                )}
                <Text><b>Créditos que a run real consumiria:</b> {testPreview.credits_needed}</Text>
                {testPreview.consolidated?.length > 0 && (
                  <>
                    <Divider style={{ margin: '8px 0' }} />
                    <Text type="warning"><b>Consolidação em aberto:</b> {testPreview.consolidated.length} contatos teriam débitos antigos somados.</Text>
                  </>
                )}
                {testPreview.recipients?.length > 0 && (
                  <>
                    <Divider style={{ margin: '8px 0' }} />
                    <Text type="secondary" style={{ fontSize: 12 }}><b>Destinatários (prévia — {testPreview.recipients.length}):</b></Text>
                    <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 4, padding: 8 }}>
                      {testPreview.recipients.map((r, i) => (
                        <div key={i} style={{ fontSize: 12, padding: '4px 0', borderBottom: i < testPreview.recipients.length - 1 ? '1px solid #fafafa' : 'none' }}>
                          <Text>{r.name}</Text>
                          <Text type="secondary" style={{ marginLeft: 8 }}>
                            {testItem?.absorb_fee
                              ? `→ recebe ${fmtBRL(r.amount_brl)} · cobra ${fmtBRL(Math.round(grossUp(r.amount_brl, testItem.methods) * 100) / 100)}`
                              : `→ ${fmtBRL(r.amount_brl)}`}
                          </Text>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </Space>
            </Card>
          </div>
        ) : (
          <Text type="secondary">Sem prévia.</Text>
        )}
      </Modal>
    </div>
  );
}

// ------------------------- Modal de criar/editar -------------------------

function RecurrenceFormModal({ open, editing, groups, teamId, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const isEdit = !!editing;

  useEffect(() => {
    if (!open) return;
    if (editing) {
      form.setFieldsValue({
        template_name: editing.template_name,
        template_description: editing.template_description || '',
        day_of_month: editing.day_of_month,
        amount_brl: Number(editing.amount_brl),
        methods_pix: !!editing.methods?.pix,
        methods_credit_card: !!editing.methods?.credit_card,
        absorb_fee: !!editing.absorb_fee,
        source_type: editing.source_type,
        source_group_id: editing.source_group_id || undefined,
        consolidate_pending: !!editing.consolidate_pending,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        source_type: 'group',
        methods_pix: true,
        methods_credit_card: true,
        absorb_fee: false,
        consolidate_pending: false,
        day_of_month: 10,
      });
    }
  }, [open, editing, form]);

  const handleSave = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      const payload = {
        team_id: teamId,
        template_name: v.template_name,
        template_description: v.template_description || null,
        day_of_month: v.day_of_month,
        amount_brl: v.amount_brl,
        methods: { pix: !!v.methods_pix, credit_card: !!v.methods_credit_card },
        absorb_fee: !!v.absorb_fee,
        source_type: v.source_type,
        source_group_id: v.source_type === 'group' ? v.source_group_id : null,
        snapshot_recipients: null,
        consolidate_pending: !!v.consolidate_pending,
      };
      if (!payload.methods.pix && !payload.methods.credit_card) {
        message.error('Escolhe pelo menos um método (PIX ou cartão)');
        setSaving(false);
        return;
      }
      if (payload.source_type === 'group' && !payload.source_group_id) {
        message.error('Escolhe um grupo de contatos');
        setSaving(false);
        return;
      }
      if (isEdit) {
        const r = await api.put(`/community/finance/charges/recurrences/${editing.id}`, payload);
        if (r.data?.applies_next_cycle) {
          message.success('Alterado. Vale a partir do próximo ciclo — cobranças já enviadas desse mês não mudam.');
        } else {
          message.success('Recorrência atualizada');
        }
      } else {
        await api.post('/community/finance/charges/recurrences', payload);
        message.success('Recorrência criada e ativada');
      }
      onSaved();
    } catch (e) {
      if (e?.errorFields) return; // validação form
      message.error(e.response?.data?.message || 'Falhou');
    } finally { setSaving(false); }
  };

  return (
    <Modal
      open={open}
      title={isEdit ? 'Editar recorrência' : 'Nova recorrência mensal'}
      onCancel={onClose}
      onOk={handleSave}
      okText={isEdit ? 'Salvar' : 'Criar e ativar'}
      confirmLoading={saving}
      width={640}
    >
      <Form form={form} layout="vertical">
        {isEdit && (
          <Alert type="warning" showIcon style={{ marginBottom: 12 }}
            message="Alterações valem no próximo ciclo"
            description="Se mudar valor, dia, fonte ou métodos, aplica só a partir da próxima cobrança automática. Cobranças já enviadas continuam como estão." />
        )}
        <Form.Item name="template_name" label="Nome do template"
          rules={[{ required: true, message: 'Nome obrigatório' }]}
          extra="Ex: “Mensalidade da pelada” — o sistema adiciona “— Setembro 2026” automaticamente todo mês.">
          <Input placeholder="Mensalidade da pelada" maxLength={200} />
        </Form.Item>
        <Form.Item name="template_description" label="Descrição (opcional)"
          extra="Aparece pro jogador no e-mail e no PIX/cartão.">
          <Input.TextArea rows={2} maxLength={500} placeholder="Mensalidade dos mensalistas da pelada de quarta" />
        </Form.Item>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="day_of_month" label="Dia do mês pra cobrar"
              rules={[{ required: true, message: 'Escolhe o dia' }]}
              extra="Se cair no dia 31 em fevereiro/etc., o sistema usa o último dia do mês.">
              <InputNumber min={1} max={31} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="amount_brl" label="Valor por pessoa (R$)"
              rules={[{ required: true, type: 'number', min: 0.01, message: 'Valor > 0' }]}>
              <InputNumber min={0.01} step={5} precision={2} style={{ width: '100%' }}
                formatter={v => `R$ ${v}`} parser={v => v.replace(/[^\d.,]/g, '').replace(',', '.')} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Métodos de pagamento aceitos">
          <Space>
            <Form.Item name="methods_pix" valuePropName="checked" noStyle><Switch /></Form.Item>
            <Text>PIX</Text>
            <Divider type="vertical" />
            <Form.Item name="methods_credit_card" valuePropName="checked" noStyle><Switch /></Form.Item>
            <Text>Cartão</Text>
          </Space>
        </Form.Item>
        <Form.Item name="absorb_fee" valuePropName="checked" label={<Space>Time absorve a taxa MP <Tooltip title="Se ligado, o jogador paga o valor bruto e o time recebe o líquido. Se desligado, o jogador paga o valor cheio e a taxa sai do time."><InfoCircleOutlined /></Tooltip></Space>}>
          <Switch />
        </Form.Item>
        <Divider>De quem cobrar</Divider>
        <Form.Item name="source_type" label="Fonte de destinatários"
          extra="Grupo = dinâmico (todo mês pega quem tá no grupo naquele momento). Recomendado pra time que ganha/perde mensalista.">
          <Select
            options={[
              { value: 'group', label: 'Grupo de contatos (dinâmico)' },
            ]}
          />
        </Form.Item>
        <Form.Item shouldUpdate={(a, b) => a.source_type !== b.source_type} noStyle>
          {({ getFieldValue }) => getFieldValue('source_type') === 'group' && (
            <Form.Item name="source_group_id" label="Grupo"
              rules={[{ required: true, message: 'Escolhe o grupo' }]}
              extra={
                groups.length === 0
                  ? <Text type="warning">Você ainda não tem grupos. Cria em Financeiro → Contatos.</Text>
                  : `${groups.length} grupo(s) disponível(is)`
              }>
              <Select
                placeholder="Selecionar grupo"
                options={groups.map(g => ({ value: g.id, label: `${g.name} (${g.member_count || g.members_count || 0} contatos)` }))}
              />
            </Form.Item>
          )}
        </Form.Item>
        <Divider>Extras</Divider>
        <Form.Item name="consolidate_pending" valuePropName="checked"
          label={<Space>Somar valores em aberto na próxima mensalidade <Tooltip title="Se um jogador tem cobrança(s) do mês passado sem pagar, o valor é somado na nova. As antigas ficam marcadas como consolidated e a nova concentra tudo."><InfoCircleOutlined /></Tooltip></Space>}
          extra="Útil pra não deixar cobrança antiga esquecida acumulando.">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
}
